/* Terra — global interaction primitives (drawers, dropdowns, modals,
   quantity inputs, carousels, sticky header, announcement rotation,
   predictive search). No dependencies. */

(() => {
  'use strict';

  const lockScroll = (locked) => {
    document.body.classList.toggle('scroll-locked', locked);
  };

  /* --- Drawer ------------------------------------------------------------ */
  class TerraDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      if (this.panel) {
        this.panel.setAttribute('tabindex', '-1');
        this.panel.focus({ preventScroll: true });
      }
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }
  }
  customElements.define('terra-drawer', TerraDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open(opener);
    }
  });

  /* --- Modal (native <dialog>) -------------------------------------------- */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      const modal = document.getElementById(opener.dataset.modalOpen);
      if (modal && typeof modal.showModal === 'function') {
        event.preventDefault();
        modal.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-modal-close]');
    if (closer) {
      const modal = closer.closest('dialog');
      if (modal) modal.close();
      return;
    }
    // Click on the backdrop closes the dialog
    const dialog = event.target.closest('dialog.modal');
    if (dialog && event.target === dialog) dialog.close();
  });

  /* --- Quantity input ------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (this.input.value !== previous) {
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* --- Details dropdown (closes on outside click / Escape) ------------------ */
  class DetailsDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) {
          this.details.open = false;
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.details.open = false;
          const summary = this.details.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    }
  }
  customElements.define('details-dropdown', DetailsDropdown);

  /* --- Scroll carousel (desktop arrows for overflow rows) --------------------- */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.querySelectorAll('[data-prev], [data-next]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.hasAttribute('data-next') ? 1 : -1;
          this.track.scrollBy({
            left: direction * this.track.clientWidth * 0.8,
            behavior: 'smooth',
          });
        });
      });
      this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
      window.addEventListener('resize', () => this.updateButtons(), { passive: true });
      this.updateButtons();
    }

    updateButtons() {
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (!prev || !next) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      prev.toggleAttribute('disabled', this.track.scrollLeft <= 2);
      next.toggleAttribute('disabled', this.track.scrollLeft >= maxScroll);
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 4;
      prev.style.display = overflowing ? '' : 'none';
      next.style.display = overflowing ? '' : 'none';
    }
  }
  customElements.define('scroll-carousel', ScrollCarousel);

  /* --- Sticky header shadow ------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      let ticking = false;
      const update = () => {
        this.classList.toggle('is-stuck', window.scrollY > 4);
        ticking = false;
      };
      window.addEventListener(
        'scroll',
        () => {
          if (!ticking) {
            window.requestAnimationFrame(update);
            ticking = true;
          }
        },
        { passive: true }
      );
      update();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* --- Announcement rotation -------------------------------------------------------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;
      this.index = 0;
      const interval = parseInt(this.dataset.interval || '5000', 10);
      setInterval(() => {
        this.slides[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.slides.length;
        this.slides[this.index].classList.add('is-active');
      }, interval);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* --- Predictive search --------------------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      let timeout;
      this.input.addEventListener('input', () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => this.search(), 250);
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const response = await fetch(url);
        if (!response.ok) return;
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('[data-predictive-inner]');
        this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (error) {
        /* network hiccup — leave previous results in place */
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* --- Share button ------------------------------------------------------------------------ */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', async () => {
        const shareData = { title: document.title, url: this.dataset.url || window.location.href };
        if (navigator.share) {
          try {
            await navigator.share(shareData);
          } catch (error) {
            /* user dismissed the share sheet */
          }
        } else {
          await navigator.clipboard.writeText(shareData.url);
          const feedback = this.querySelector('[data-share-feedback]');
          if (feedback) {
            feedback.hidden = false;
            setTimeout(() => (feedback.hidden = true), 2000);
          }
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);
})();
